import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { BuildingOffice2Icon, NoSymbolIcon, UserIcon } from "@heroicons/react/24/outline";
import { Table, TableHead, TableRow, TableHeader, TableBody, TableCell } from "../../Catalyst/table";
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from "../../Catalyst/dropdown";
import { OrganizationV3 } from "../../../store/slices/OrganizationsV3Slice";
import { ORG_STATUS, ORG_STATUS_DISPLAY } from "../../../constants/organizations";
import { formatStatusDate } from "./utils";

interface OrganizationsTableProps {
  organizations: OrganizationV3[];
  // Coach is read-only: hides the Deactivate action (the server 403s it anyway).
  canManage: boolean;
  onOpenOrganization: (organization: OrganizationV3) => void;
  onViewUsers: (organization: OrganizationV3) => void;
  onDeactivate: (organization: OrganizationV3) => void;
}

const OrganizationsTable = ({
  organizations,
  canManage,
  onOpenOrganization,
  onViewUsers,
  onDeactivate,
}: OrganizationsTableProps) => {
  return (
    <Table>
      <TableHead>
        <TableRow className="bg-[#F9FAFB] border border-x-[#EAECF0]">
          <TableHeader>Name</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader>Number of Active Users</TableHeader>
          <TableHeader>Number of Active Sub-Orgs</TableHeader>
          <TableHeader className="text-right">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {organizations.map((organization) => {
          const statusConfig = ORG_STATUS_DISPLAY[organization.status];
          const statusDate = formatStatusDate(organization.statusDate);
          return (
            <TableRow key={organization.id}>
              <TableCell
                onClick={() => onOpenOrganization(organization)}
                className="cursor-pointer font-medium text-texthead"
              >
                {organization.name}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {statusConfig ? (
                    <span
                      className={`rounded-full px-3 py-1 text-sm font-medium ${statusConfig.pillClass}`}
                    >
                      {statusConfig.label}
                    </span>
                  ) : (
                    <span className="text-textColor">{organization.status}</span>
                  )}
                  {/* statusDate can be null on legacy rows — render the pill alone. */}
                  {statusConfig && statusDate && (
                    <span className="text-sm font-medium text-textColor">
                      {statusConfig.datePrefix} {statusDate}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-texthead">{organization.activeUsers}</TableCell>
              <TableCell className="text-texthead">{organization.activeSubOrgs}</TableCell>
              <TableCell className="text-right">
                <Dropdown>
                  <DropdownButton outline aria-label={`Actions for ${organization.name}`}>
                    <EllipsisHorizontalIcon />
                  </DropdownButton>
                  <DropdownMenu anchor="bottom end">
                    <DropdownItem onClick={() => onOpenOrganization(organization)}>
                      <BuildingOffice2Icon />
                      <DropdownLabel>View Sub Organizations</DropdownLabel>
                    </DropdownItem>
                    <DropdownItem onClick={() => onViewUsers(organization)}>
                      <UserIcon />
                      <DropdownLabel>View Users</DropdownLabel>
                    </DropdownItem>
                    {/* Deactivation is one-way; only offered on active orgs. */}
                    {canManage && organization.status === ORG_STATUS.ACTIVE && (
                      <DropdownItem onClick={() => onDeactivate(organization)}>
                        <NoSymbolIcon />
                        <DropdownLabel>Deactivate</DropdownLabel>
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                </Dropdown>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default OrganizationsTable;
